/**
 * TraceExplainer — explainer for the TRACE Structural Duality Principle (Tr).
 *
 * TRACE is the level-1 SDP that pairs with DATA.LINEAGE along the
 * fibration ⊣ opfibration adjunction. It certifies that provenance
 * coverage and sensitivity coverage compose bidirectionally — a
 * DATA.LINEAGE witness can be stacked through arbitrary refactorings
 * without producing a forbidden pattern. With DATA.LINEAGE (primal) it
 * closes as the ICG-Tr Safety Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CalloutBox,
  ExplainerCard,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.TRACE.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.TRACE.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-trace",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "TRACE",
  subtitle: "Tr · fibration ↔ opfibration · Neutralization Theorems: D1 + D2",
  accent: ACCENT,
  accentAlt: DUAL_PAIR_COLOR["DATA.LINEAGE"].color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "DATA.LINEAGE (primal DPG)",
    href: "#explainer-data-lineage",
    accent: DUAL_PAIR_COLOR["DATA.LINEAGE"].color,
  },
  {
    label: "ICG-Tr (Safety Envelope)",
    href: "#explainer-icg-tr",
    accent: DUAL_PAIR_COLOR["DATA.LINEAGE"].color,
  },
  {
    label: "DATA.ORIGIN (D1)",
    href: "#explainer-data-origin",
    accent: THEOREM_COLOR["DATA.ORIGIN"],
  },
  { label: "DATA.REACH (D2)", href: "#explainer-data-reach", accent: THEOREM_COLOR["DATA.REACH"] },
  { label: "Fibration", href: "#explainer-fibration" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>TRACE</strong> is the safety story for stacking lineage: it certifies that "provenance
      and sensitivity coverage" composes safely with other guarantees — by the fibration ↔
      opfibration duality, not by inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "this value's origin and reach are both pinned" (a DATA.LINEAGE witness),
        you immediately want to stack two of them across a refactor. TRACE is the certificate that
        says stacking is safe — the joint trace is itself a DATA.LINEAGE witness, not a weaker one.
      </p>
      <p>
        Where DATA.LINEAGE delivers the guarantee inside one regime, TRACE delivers compositionality
        across regimes. Together they close as the ICG-Tr Safety Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        TRACE is the Structural Duality Principle that pairs the provenance fibration and the
        sensitivity opfibration at the SDP level: cartesian reindex ⊣ opcartesian pushforward.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the DATA.LINEAGE seam declares an <em>absolute empty hom-set</em>: the
        origin-only-or-reach-only morphism either exists or it does not, and the forbidden Kan
        extension is unconstructable in the strict binary sense. The TRACE adjunction operates at
        the <strong>2-categorical, graded</strong> level: its unit supplies, <em>at each grade</em>,
        the precise governance datum a bridge must produce to cross the lineage seam — the crossing
        is structurally inexpressible without the witness, but expressible with it.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        TRACE is what lets the ICG-Tr Safety Envelope reduce the audit surface from "a DATA.LINEAGE
        witness per regime" to "one envelope" carrying the adjunction unit and counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If two lineage envelopes stack but the joint is
        not a DATA.LINEAGE witness, TRACE rules it out structurally — there is no graded-adjunction
        unit certifying the stack. The forbidden composition is <em>structurally inexpressible</em>{" "}
        at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Tr" accent={ACCENT}>
        DATA.LINEAGE (DPG) ⊗ TRACE (SDP) = ICG-Tr, the single two-sided certificate. Audit surface
        collapses from <em>(DPG witness) + (SDP witness)</em> to <em>one envelope</em> carrying the
        fibration ⊣ opfibration unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "DATA.ORIGIN kernel module",
      path: "agda/Kernel/DATA/ORIGIN/Neutralization.agda",
    },
    {
      label: "DATA.REACH kernel module",
      path: "agda/Kernel/DATA/REACH/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <AdjunctionDiagram
      leftCat="Fib (origin)"
      rightCat="Opfib (reach)"
      leftAdjoint="f*"
      rightAdjoint="f₊"
      accent={ACCENT}
      ariaLabel="TRACE duality: cartesian reindex left-adjoint to opcartesian pushforward with the graded crossing unit and counit"
    />
  </>
);

export function TraceExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
